// Utilities & hooks
import { useUserUpdateProfileSettings } from "../../api/User/User";
import { USER_PROFILE_SETTINGS_UPDATE } from "../../schemas/UserSchemas";
import { useAuth } from "../../providers/auth-context";
import { useBackNavigation } from "../../hooks/useBackNavigation";
import {
  useTwoFactorAuthConfirm,
  useTwoFactorAuthDisable,
  useTwoFactorAuthSetup,
} from "../../api/2FA/2FA";
import { useAuthenticationLogoutFromAllDevices } from "../../api/Authentication/Authentication";
import useErrorReporting from "../../hooks/useErrorReporting";

// Components
import { Formik, Form, Field, FormikValues } from "formik";
import Card from "../../components/Card/Card";
import Button from "../../components/Button/Button";
import ContentHeader from "../../components/Content/ContentHeader";
import Skeleton from "react-loading-skeleton";
import FormPasswordInput from "../../components/Form/FormPasswordInput";
import FormTwoFactorAuth from "../../components/Form/FormTwoFactorAuth";
import Modal from "../../components/Modal/Modal";

// Interfaces
import { UserUpdateProfileSettingsPayload } from "../../api/User/interfaces";
import { useEffect, useState } from "react";

// Schemas
import { TWO_FACTOR_AUTH_OTP_SCHEMA } from "../../schemas/TwoFactorAuthSchemas";

// Assets
import { FaApple as AppleLogo } from "react-icons/fa";
import { BsAndroid2 as AndroidLogo } from "react-icons/bs";

const ProfileSettings = () => {
  const { user } = useAuth();
  const errorReporting = useErrorReporting();

  /*==============================
    UPDATE SETTINGS
  ===============================*/
  const updateProfileSettings = useUserUpdateProfileSettings();

  const handleUpdateProfileSettings = async (values: UserUpdateProfileSettingsPayload) => {
    try {
      await updateProfileSettings.mutateAsync(values);
    } catch (error) {
      errorReporting("Failed updating profile settings", error, { ...values });
    }
  };

  /*==============================
    CUSTOM BACK NAVIGATION
  ===============================*/
  const handleNavigateBack = useBackNavigation();

  /*==============================
    LOGOUT FROM ALL DEVICES
  ===============================*/
  const logoutAllDevices = useAuthenticationLogoutFromAllDevices();
  const handleLogoutFromAllDevices = async () => {
    try {
      await logoutAllDevices.mutateAsync();
    } catch (error) {
      errorReporting("Failed logging out from all devices", error);
    }
  };

  /*==============================
    2FA
  ===============================*/
  const [confirmModalIsOpen, setConfirmModalIsOpen] = useState<boolean>(false);
  const [disableModalIsOpen, setDisableModalIsOpen] = useState<boolean>(false);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [formattedSecret, setFormattedSecret] = useState<string | null>(null);

  // Format the received secret key into more visible and presentable
  // set of chunks consisted of 4 characters each
  useEffect(() => {
    if (!secret) return;

    const secretChunks: string[][] = [];
    const secretSplit: string[] = secret.split("");

    for (let i = 0; i < secretSplit.length; i += 4) {
      secretChunks.push(secretSplit.slice(i, i + 4));
    }

    setFormattedSecret(secretChunks.map((chunk: string[]) => chunk.join("")).join(" "));
  }, [secret]);

  // Generate neceesary secret and QR codes
  const setupTwoFactorAuth = useTwoFactorAuthSetup();
  const handleSetupTwoFactorAuth = async () => {
    try {
      const response = await setupTwoFactorAuth.mutateAsync();

      setConfirmModalIsOpen(true);
      setQrCode(response.qr_code);
      setSecret(response.google2fa_secret);
    } catch (error) {
      errorReporting("Failed setting up 2FA", error);
    }
  };

  // Confirm and enable 2FA for the current account
  const confirmTwoFactorAuth = useTwoFactorAuthConfirm();
  const handleConfirmTwoFactorAuth = async (values: FormikValues) => {
    if (!secret) return;

    try {
      await confirmTwoFactorAuth.mutateAsync({ otp: values.otp, google2fa_secret: secret });
      handleCloseConfirmationModal();
    } catch (error) {
      errorReporting("Failed confirming 2FA", error);
    }
  };

  // Disable and remove 2FA from the current account
  const disableTwoFactorAuth = useTwoFactorAuthDisable();
  const handleDisableTwoFactorAuth = async () => {
    try {
      await disableTwoFactorAuth.mutateAsync();
      setDisableModalIsOpen(false);
    } catch (error) {
      errorReporting("Failed disabling 2FA", error);
    }
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalIsOpen(false);
    setQrCode(null);
    setSecret(null);
  };

  return (
    <div className="container py--25">
      <ContentHeader title="Profile Settings" modifierClass="mb--25" />
      <Card modifierClass="card--padding--xl">
        {user.is_2fa_enabled ? (
          <div className="mb--40">
            <h5 className="txt--blue-light txt--lg fw--semibold">
              Two-factor Authentication (2FA)
            </h5>
            <p>Your account has 2FA enabled.</p>
            <Button
              modifierClass="btn--danger btn--fluid"
              onClick={() => setDisableModalIsOpen(true)}
              isLoading={disableTwoFactorAuth.isPending}
              isDisabled={disableTwoFactorAuth.isPending}
            >
              Disable 2FA
            </Button>
          </div>
        ) : (
          <div className="mb--40">
            <h5 className="txt--blue-light txt--lg fw--semibold">
              Two-factor Authentication (2FA)
            </h5>
            <p>Setup the two-factor authentication flow for your account.</p>
            <Button
              modifierClass="btn--primary btn--fluid"
              onClick={handleSetupTwoFactorAuth}
              isLoading={setupTwoFactorAuth.isPending}
              isDisabled={setupTwoFactorAuth.isPending}
            >
              Enable 2FA
            </Button>
          </div>
        )}

        <div className="mb--40">
          <h5 className="txt--blue-light txt--lg fw--semibold">Logout from all devices</h5>
          <p>
            You will be logged out from all devices where this account was used to login into the
            application.
          </p>
          <Button
            modifierClass="btn--danger btn--fluid"
            onClick={handleLogoutFromAllDevices}
            isLoading={logoutAllDevices.isPending}
            isDisabled={logoutAllDevices.isPending}
          >
            Logout from all devices
          </Button>
        </div>

        <div className="table-wrapper table-wrapper--no-shadow px--0i py--0i">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ width: "50%", wordBreak: "break-word" }}>
                  {!user.first_name ? (
                    <Skeleton width="250px" height="15px" />
                  ) : (
                    <>
                      {user.first_name} {user.last_name}
                    </>
                  )}
                </td>
                <td style={{ width: "50%", wordBreak: "break-word" }}>
                  {!user.email ? <Skeleton width="200px" height="15px" /> : user.email}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="fw--semibold txt--md txt--black mb--40">
          <span className="txt--blue-light txt--lg d-block">Did you know?</span>
          Security experts agree you should change your password a minimum of every 90 days.
        </p>

        <Formik
          initialValues={{ current_password: "", new_password: "", new_password_confirmation: "" }}
          validationSchema={USER_PROFILE_SETTINGS_UPDATE}
          enableReinitialize
          onSubmit={handleUpdateProfileSettings}
        >
          <Form className="container--xxs mx--auto">
            <Field
              component={FormPasswordInput}
              id="current_password"
              name="current_password"
              placeholder="Enter current password"
              modifierClass="mb--15"
            />

            <Field
              component={FormPasswordInput}
              id="new_password"
              name="new_password"
              placeholder="Enter new Password"
              modifierClass="mb--15"
            />

            <Field
              component={FormPasswordInput}
              id="new_password_confirmation"
              name="new_password_confirmation"
              placeholder="Confirm new password"
              modifierClass="mb--15"
            />

            <div className="d-flex flex-column flex-md-row justify-content-md-start align-items-center">
              {user.active_company.slug ? (
                <Button
                  type="button"
                  modifierClass="btn--fluid btn--fluid--md btn--primary--light fw--semibold mb--md--10"
                  onClick={() => handleNavigateBack("/applications/")}
                >
                  Cancel
                </Button>
              ) : null}

              <Button
                modifierClass="btn--fluid btn--fluid--md btn--primary fw--semibold ml--10 ml--md--0"
                isLoading={updateProfileSettings.isPending}
                isDisabled={updateProfileSettings.isPending}
              >
                Submit
              </Button>
            </div>
          </Form>
        </Formik>
      </Card>

      {confirmModalIsOpen ? (
        <Modal
          handleCloseModal={handleCloseConfirmationModal}
          title="Confirm Two-Factor Authentication"
          text={""}
          modifierClass="modal--lg modal--2fa"
        >
          <Formik
            initialValues={{ otp: "" }}
            onSubmit={handleConfirmTwoFactorAuth}
            validationSchema={TWO_FACTOR_AUTH_OTP_SCHEMA}
          >
            <Form>
              <h4>Follow the steps below to set up your two-factor authentication app</h4>

              <h6>1.) Download a two-factor authentication app.</h6>
              <div>
                <div className="twofactor-links">
                  <AppleLogo />
                  <p>
                    iPhone, iPod Touch, or iPad:{" "}
                    <a
                      href="https://apps.apple.com/us/app/google-authenticator/id388497605"
                      target="_blank"
                    >
                      Google Authenticator
                    </a>
                    or
                    <a
                      href="https://apps.apple.com/us/app/microsoft-authenticator/id983156458"
                      target="_blank"
                    >
                      Microsoft Authenticator.
                    </a>
                  </p>
                </div>

                <div className="twofactor-links">
                  <AndroidLogo />
                  <p>
                    Android devices:
                    <a
                      href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=en"
                      target="_blank"
                    >
                      Google Authenticator
                    </a>
                    or
                    <a
                      href="https://play.google.com/store/apps/details?id=com.azure.authenticator&hl=en"
                      target="_blank"
                    >
                      Microsoft Authenticator.
                    </a>
                  </p>
                </div>
              </div>

              <hr />

              <h6>
                2.) Tap the plus sign or QR code button in your authenticator app to add a new
                account. Then scan the QR code below.
              </h6>

              <div className="d-flex flex-column flex-md-row justify-content-center align-items-start">
                {qrCode ? <img src={qrCode} /> : null}

                <div className="twofactor-details">
                  <p>
                    <strong>Can't scan the code?</strong>You can add the code to your application
                    manually using the following details:{" "}
                  </p>
                  <p>
                    <strong>Account:</strong>
                    {user.email}
                  </p>
                  <p>
                    <strong>Key:</strong>
                    {formattedSecret}
                  </p>
                </div>
              </div>

              <hr />

              <h6>3.) Enter the generated authenticator code below. </h6>
              <Field component={FormTwoFactorAuth} id="otp" name="otp" />

              <Button
                modifierClass="btn--primary btn--fluid mt--20"
                isDisabled={confirmTwoFactorAuth.isPending}
                isLoading={confirmTwoFactorAuth.isPending}
              >
                Submit
              </Button>
            </Form>
          </Formik>
        </Modal>
      ) : null}

      {disableModalIsOpen ? (
        <Modal
          handleCloseModal={() => setDisableModalIsOpen(false)}
          title="Disable Two-Factor Authentication"
          text="Are your sure you want to disable and remove 2FA from your account?"
        >
          <div className="modal__actions">
            <Button
              modifierClass="btn--secondary btn--fluid"
              isDisabled={disableTwoFactorAuth.isPending}
              type="button"
              onClick={() => setDisableModalIsOpen(false)}
            >
              Cancel
            </Button>

            <Button
              modifierClass="btn--primary btn--fluid"
              onClick={handleDisableTwoFactorAuth}
              isDisabled={disableTwoFactorAuth.isPending}
              isLoading={disableTwoFactorAuth.isPending}
              type="button"
            >
              Yes, Disable it
            </Button>
          </div>
        </Modal>
      ) : null}
    </div>
  );
};
export default ProfileSettings;
